/**
 * Horizon Pro — Product Card Enhancements
 * Stock counter, swatch hover image preview, inline variant picker.
 * Reads config from body data attributes.
 */
(function () {
  'use strict';

  var body = document.body;
  var stockMode = body.dataset.hpCardStock || 'none';
  var lowThreshold = parseInt(body.dataset.hpCardLowThreshold) || 10;
  var variantPicker = body.dataset.hpCardVariantPicker || 'none';
  var swatchPreview = body.dataset.hpCardSwatchPreview === 'true';

  if (stockMode === 'none' && variantPicker === 'none') return;

  /**
   * Stock counter — injects stock level indicator after the price block
   */
  function injectStockCounter(card) {
    if (card.querySelector('.hp-stock-counter')) return;
    var productId = card.dataset.productId;
    if (!productId) return;

    // Find price element to insert after
    var priceEl = card.querySelector('.price');
    if (!priceEl) return;

    // Fetch product data from Shopify AJAX API
    fetch('/products/' + getProductHandle(card) + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        var variant = product.variants[0]; // default to first variant
        // Try to find selected variant
        var selectedId = card.querySelector('input[name="id"]');
        if (selectedId && selectedId.value) {
          var found = product.variants.find(function (v) { return v.id == selectedId.value; });
          if (found) variant = found;
        }

        if (!variant) return;

        var inventory = variant.inventory_quantity;
        var policy = variant.inventory_policy;
        var mgmt = variant.inventory_management;

        // Don't show for "continue selling" products or untracked
        if (!mgmt || policy === 'continue') return;

        var isLow = inventory > 0 && inventory <= lowThreshold;
        var inStock = inventory > lowThreshold;

        if (stockMode === 'low' && !isLow) return;
        if (inventory <= 0) return;

        var counter = document.createElement('div');
        counter.className = 'hp-stock-counter ' + (isLow ? 'hp-stock-counter--low' : 'hp-stock-counter--in-stock');
        counter.innerHTML = '<span class="hp-stock-counter__dot"></span><span class="hp-stock-counter__text">' +
          (isLow ? 'Only ' + inventory + ' left' : 'In stock') + '</span>';

        priceEl.parentNode.insertBefore(counter, priceEl.nextSibling);
      })
      .catch(function () {});
  }

  /**
   * Get product handle from card link
   */
  function getProductHandle(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';
    var match = link.href.match(/\/products\/([^?#/]+)/);
    return match ? match[1] : '';
  }

  /**
   * Inline variant picker — renders size/color options directly on cards
   */
  function injectVariantPicker(card) {
    if (card.querySelector('.hp-card-variants')) return;
    var handle = getProductHandle(card);
    if (!handle) return;

    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        if (product.variants.length <= 1) return;

        var priceEl = card.querySelector('.price');
        var insertAfter = card.querySelector('.hp-stock-counter') || priceEl;
        if (!insertAfter) return;

        product.options.forEach(function (option, optIndex) {
          var isColor = /colou?r|shade|finish/i.test(option.name);

          // In swatches-only mode, skip non-color options
          if (variantPicker === 'swatches' && !isColor) return;

          var wrapper = document.createElement('div');
          wrapper.className = 'hp-card-variants';
          wrapper.dataset.optionIndex = optIndex;
          wrapper.dataset.optionName = option.name;

          var values = [];
          product.variants.forEach(function (v) {
            var val = v.options[optIndex];
            if (values.indexOf(val) === -1) values.push(val);
          });

          // Show max 6 values, +N overflow
          var maxShow = isColor ? 8 : 5;
          var shown = values.slice(0, maxShow);
          var overflow = values.length - maxShow;

          shown.forEach(function (val, valIdx) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.dataset.value = val;
            btn.dataset.optionIndex = optIndex;
            btn.setAttribute('aria-label', option.name + ': ' + val);

            // Check availability
            var available = product.variants.some(function (v) {
              return v.options[optIndex] === val && v.available;
            });

            if (isColor) {
              btn.className = 'hp-card-variants__color' + (valIdx === 0 ? ' hp-card-variants__color--selected' : '') + (!available ? ' hp-card-variants__option--unavailable' : '');
              // Try to get swatch color from value name
              var colorMap = getColorFromName(val);
              btn.style.backgroundColor = colorMap;
              btn.title = val;
            } else {
              btn.className = 'hp-card-variants__option' + (valIdx === 0 ? ' hp-card-variants__option--selected' : '') + (!available ? ' hp-card-variants__option--unavailable' : '');
              btn.textContent = val;
            }

            if (!available) {
              btn.disabled = true;
            }

            // Click handler
            btn.addEventListener('click', function () {
              if (!available) return;
              // Update selection
              wrapper.querySelectorAll('[data-value]').forEach(function (b) {
                b.classList.remove('hp-card-variants__option--selected', 'hp-card-variants__color--selected');
              });
              btn.classList.add(isColor ? 'hp-card-variants__color--selected' : 'hp-card-variants__option--selected');

              // Find matching variant
              var selectedOptions = getSelectedOptions(card, product);
              selectedOptions[optIndex] = val;
              var matchedVariant = findVariant(product, selectedOptions);

              if (matchedVariant) {
                // Update hidden input
                var idInput = card.querySelector('input[name="id"]');
                if (idInput) idInput.value = matchedVariant.id;

                // Update price
                updatePrice(card, matchedVariant, product);

                // Update image if color swatch
                if (isColor && matchedVariant.featured_image) {
                  updateCardImage(card, matchedVariant.featured_image.src);
                }

                // Update quick-add button state
                updateQuickAdd(card, matchedVariant, product);
              }
            });

            // Hover preview for color swatches
            if (isColor && swatchPreview) {
              btn.addEventListener('mouseenter', function () {
                var hoverVariant = product.variants.find(function (v) {
                  return v.options[optIndex] === val && v.featured_image;
                });
                if (hoverVariant && hoverVariant.featured_image) {
                  updateCardImage(card, hoverVariant.featured_image.src);
                }
              });
              btn.addEventListener('mouseleave', function () {
                // Return to selected variant image
                var selectedColor = wrapper.querySelector('.hp-card-variants__color--selected');
                if (selectedColor) {
                  var selVal = selectedColor.dataset.value;
                  var selVariant = product.variants.find(function (v) {
                    return v.options[optIndex] === selVal && v.featured_image;
                  });
                  if (selVariant && selVariant.featured_image) {
                    updateCardImage(card, selVariant.featured_image.src);
                  }
                }
              });
            }

            wrapper.appendChild(btn);
          });

          if (overflow > 0) {
            var more = document.createElement('span');
            more.className = 'hp-card-variants__overflow';
            more.textContent = '+' + overflow;
            wrapper.appendChild(more);
          }

          // Inject inside the card gallery for hover overlay
          var gallery = card.querySelector('.card-gallery, [ref="cardGallery"]');
          if (gallery) {
            gallery.appendChild(wrapper);
            card.classList.add('hp-card--has-variants');
          } else {
            // Fallback to old behavior
            insertAfter.parentNode.insertBefore(wrapper, insertAfter.nextSibling);
          }
          insertAfter = wrapper;
        });
      })
      .catch(function () {});
  }

  function getSelectedOptions(card, product) {
    var options = [];
    product.options.forEach(function (opt, i) {
      var picker = card.querySelector('[data-option-index="' + i + '"]');
      if (picker) {
        var selected = picker.querySelector('.hp-card-variants__option--selected, .hp-card-variants__color--selected');
        options.push(selected ? selected.dataset.value : product.variants[0].options[i]);
      } else {
        options.push(product.variants[0].options[i]);
      }
    });
    return options;
  }

  function findVariant(product, selectedOptions) {
    return product.variants.find(function (v) {
      return selectedOptions.every(function (opt, i) {
        return v.options[i] === opt;
      });
    });
  }

  function updatePrice(card, variant, product) {
    // Horizon uses <product-price> with <span class="price"> and <span class="compare-at-price">
    var productPrice = card.querySelector('product-price');
    if (!productPrice) return;

    // Update the main price (Horizon: span.price inside [ref="priceContainer"])
    var priceSpan = productPrice.querySelector('span.price');
    if (priceSpan) {
      priceSpan.textContent = formatMoney(variant.price);
    }

    // Update compare-at price
    var compareSpan = productPrice.querySelector('span.compare-at-price');
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (compareSpan) {
        compareSpan.textContent = formatMoney(variant.compare_at_price);
        compareSpan.closest('[role="group"]').style.display = '';
      }
    } else if (compareSpan) {
      var group = compareSpan.closest('[role="group"]');
      if (group) group.style.display = 'none';
    }
  }

  function formatMoney(cents) {
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: window.Shopify && Shopify.currency ? Shopify.currency.active : 'USD' });
  }

  function updateCardImage(card, src) {
    var img = card.querySelector('.card-gallery img');
    if (!img) return;
    // Generate appropriate size URL
    var sized = src.replace(/\.(jpg|jpeg|png|webp|gif)/, '_600x$&').replace('https:', '');
    img.src = sized;
    img.srcset = '';
  }

  function updateQuickAdd(card, variant, product) {
    var quickAdd = card.querySelector('quick-add-component');
    if (!quickAdd) return;
    var idInput = quickAdd.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = variant.id;
      idInput.disabled = !variant.available;
    }
    // Switch to "Add" mode since variant is selected
    if (product.options.length === 1 || getSelectedOptions(card, product).every(Boolean)) {
      quickAdd.dataset.quickAddButton = variant.available ? 'add' : 'choose';
    }
  }

  /**
   * Simple color name → hex map for common color swatches
   */
  function getColorFromName(name) {
    var colors = {
      black: '#000000', white: '#ffffff', red: '#dc2626', blue: '#2563eb',
      green: '#16a34a', yellow: '#eab308', orange: '#ea580c', pink: '#ec4899',
      purple: '#9333ea', brown: '#92400e', grey: '#6b7280', gray: '#6b7280',
      navy: '#1e3a5f', beige: '#d2b48c', cream: '#fffdd0', gold: '#d4af37',
      silver: '#c0c0c0', tan: '#d2b48c', coral: '#ff6b6b', teal: '#0d9488',
      mint: '#98f5e1', lavender: '#e6e6fa', olive: '#808000', burgundy: '#800020',
      charcoal: '#36454f', ivory: '#fffff0', khaki: '#c3b091', maroon: '#800000',
      natural: '#f5f0e8', nude: '#e8c4a0', rust: '#b7410e', sage: '#9dc183',
      sand: '#c2b280', slate: '#708090', stone: '#928e85', wine: '#722f37',
      'light blue': '#87ceeb', 'dark blue': '#00008b', 'dark green': '#006400',
      'light grey': '#d3d3d3', 'light gray': '#d3d3d3', 'dark grey': '#404040',
      'dark gray': '#404040', 'hot pink': '#ff69b4', 'rose gold': '#b76e79',
      'sky blue': '#87ceeb', 'forest green': '#228b22', 'royal blue': '#4169e1'
    };
    var lower = name.toLowerCase().trim();
    return colors[lower] || '#' + hashColor(lower);
  }

  /** Generate a consistent color from string hash for unknown colors */
  function hashColor(str) {
    var hash = 0;
    for (var i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    var h = Math.abs(hash % 360);
    return hslToHex(h, 45, 55);
  }

  function hslToHex(h, s, l) {
    s /= 100; l /= 100;
    var a = s * Math.min(l, 1 - l);
    var f = function (n) {
      var k = (n + h / 30) % 12;
      var color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return f(0) + f(8) + f(4);
  }

  /* ── Init ── */
  function enhanceCards(root) {
    var cards = (root || document).querySelectorAll('product-card:not([data-hp-enhanced])');
    cards.forEach(function (card) {
      card.dataset.hpEnhanced = 'true';
      if (stockMode !== 'none') injectStockCounter(card);
      if (variantPicker !== 'none') injectVariantPicker(card);
    });
  }

  // Run on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { enhanceCards(); });
  } else {
    enhanceCards();
  }

  // Watch for dynamically added cards
  var mo = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var nodes = mutations[i].addedNodes;
      for (var j = 0; j < nodes.length; j++) {
        var node = nodes[j];
        if (node.nodeType !== 1) continue;
        if (node.tagName === 'PRODUCT-CARD') enhanceCards(node.parentNode);
        if (node.querySelector && node.querySelector('product-card')) enhanceCards(node);
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  // Theme editor re-render
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) {
      e.target.querySelectorAll('product-card').forEach(function (c) {
        delete c.dataset.hpEnhanced;
        var existing = c.querySelectorAll('.hp-stock-counter, .hp-card-variants');
        existing.forEach(function (el) { el.remove(); });
      });
      enhanceCards(e.target);
    }
  });
})();
